import { Header } from "@/components/Header";
import { HeroSection } from "@/components/sections/HeroSection";
import { ProblemSection } from "@/components/sections/ProblemSection";
import { PromiseSection } from "@/components/sections/PromiseSection";
import { SystemSection } from "@/components/sections/SystemSection";
import { ComparisonSection } from "@/components/sections/ComparisonSection";
import { WhyWorksSection } from "@/components/sections/WhyWorksSection";
import { ResultsSection } from "@/components/sections/ResultsSection";
import { TradesSection } from "@/components/sections/TradesSection";
import { ProcessSection } from "@/components/sections/ProcessSection";
import { FitSection } from "@/components/sections/FitSection";
import { FAQSection } from "@/components/sections/FAQSection";
import { ContactSection } from "@/components/sections/ContactSection";
import { Footer } from "@/components/Footer";
import { MobileStickyCTA } from "@/components/MobileStickyCTA";
import { Helmet } from "react-helmet-async";

const Index = () => {
  const siteUrl = "https://fullscalemarketing.com.au";
  const siteName = "FullScale Marketing";
  const siteTitle = "FullScale Marketing | Digital Marketing for Tradies Australia";
  const siteDescription = "FullScale Marketing helps tradies, construction companies and home service businesses with websites, Google reviews, local SEO, and digital marketing that actually brings in more jobs.";
  const siteImage = "https://fullscalemarketing.com.au/og-image.jpg";

  // Organization Schema (exact as provided)
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "FullScale Marketing",
    url: "https://fullscalemarketing.com.au",
    logo: "https://fullscalemarketing.com.au/og-image.jpg",
    image: "https://fullscalemarketing.com.au/og-image.jpg",
    description: "Digital marketing agency for tradies, construction companies and home service businesses. We provide local SEO, websites, Google reviews, and lead generation.",
    sameAs: [
      "https://www.facebook.com/fullscalemarketing",
      "https://www.instagram.com/fullscalemarketing",
      "https://www.linkedin.com/company/fullscalemarketing"
    ],
  };

  // Comprehensive Schema Markup
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "MarketingAgency",
    "@id": `${siteUrl}/#organization`,
    name: siteName,
    alternateName: "FullScale Marketing Australia",
    description: "Digital marketing agency specialising in tradies, construction companies and home service businesses across Australia. We help trade businesses get more leads, reviews and jobs through proven digital growth systems.",
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    image: siteImage,
    telephone: "+61",
    email: "info@fullscalemarketing.com.au",
    address: {
      "@type": "PostalAddress",
      addressCountry: "AU",
      addressRegion: "Australia-wide",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: -33.8688,
      longitude: 151.2093,
    },
    areaServed: [
      { "@type": "Country", name: "Australia" },
      { "@type": "City", name: "Sydney" },
      { "@type": "City", name: "Melbourne" },
      { "@type": "City", name: "Brisbane" },
      { "@type": "City", name: "Perth" },
      { "@type": "City", name: "Adelaide" },
      { "@type": "City", name: "Gold Coast" },
      { "@type": "City", name: "Newcastle" },
      { "@type": "City", name: "Canberra" },
    ],
    knowsAbout: [
      "Digital Marketing for Tradies",
      "SEO for Trade Businesses",
      "Google Ads for Construction Companies",
      "Facebook Ads for Tradies",
      "Website Design for Tradies",
      "Google Reviews Management",
      "Lead Generation for Home Services",
      "Local SEO Australia",
    ],
    sameAs: [
      "https://www.instagram.com/fullscale_marketing/",
      "https://www.facebook.com/p/FullScale-Marketing-61562089223781/",
    ],
    priceRange: "$$",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "5",
      reviewCount: "47",
      bestRating: "5",
      worstRating: "1",
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Digital Marketing Services for Tradies",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Tradie Website Design",
            description: "Professional, mobile-first websites designed specifically for Australian trade businesses to convert visitors into leads.",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Local SEO for Tradies",
            description: "Get found in your target suburbs with Google Business Profile optimisation and local search ranking strategies.",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Google Ads for Tradies",
            description: "Targeted Google Ads campaigns that bring qualified leads from customers actively searching for trade services.",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Facebook Ads for Tradies",
            description: "Social media advertising campaigns designed to generate quality leads for construction and home service businesses.",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Google Reviews Management",
            description: "Automated 5-star review collection system to build trust and improve local search rankings for trade businesses.",
          },
        },
      ],
    },
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    url: siteUrl,
    name: siteName,
    description: siteDescription,
    publisher: {
      "@id": `${siteUrl}/#organization`,
    },
    inLanguage: "en-AU",
    potentialAction: {
      "@type": "SearchAction",
      target: `${siteUrl}/?s={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is the best marketing for tradies in Australia?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "The best marketing for tradies combines local SEO, Google Business Profile optimisation, targeted Google and Facebook Ads, a professional website, and an automated review collection system. FullScale Marketing provides all these services in one integrated growth system designed specifically for Australian trade businesses.",
        },
      },
      {
        "@type": "Question",
        name: "How much does digital marketing cost for tradies?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Pricing depends on your business needs and goals. After a quick audit, we provide a clear proposal with exact costs, timelines and expected outcomes — no hidden fees. Most clients see ROI within 2-4 weeks through increased enquiries and booked jobs.",
        },
      },
      {
        "@type": "Question",
        name: "How can tradies get more Google reviews?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "We implement an automated review collection system that sends review requests at the right time, making it easy for happy customers to leave 5-star reviews. Most clients see their Google reviews triple within 2 months.",
        },
      },
      {
        "@type": "Question",
        name: "Do you work with tradies Australia-wide?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes, we work with trade, construction and home service businesses across all of Australia including Sydney, Melbourne, Brisbane, Perth, Adelaide, Gold Coast, Newcastle, Canberra and regional areas.",
        },
      },
      {
        "@type": "Question",
        name: "How do I get more leads as a tradie?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "To get more leads as a tradie, you need to be visible where customers search (Google, social media), look trustworthy (professional website, strong reviews), and respond fast (automation). Our digital growth system addresses all three factors to deliver consistent, qualified leads every week.",
        },
      },
    ],
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Digital Marketing for Trade Businesses",
    provider: {
      "@id": `${siteUrl}/#organization`,
    },
    areaServed: {
      "@type": "Country",
      name: "Australia",
    },
    description: "Complete digital growth system for Australian tradies, construction companies and home service businesses. Includes websites, SEO, Google Ads, Facebook Ads, review management and lead automation.",
    offers: {
      "@type": "Offer",
      availability: "https://schema.org/InStock",
      priceSpecification: {
        "@type": "PriceSpecification",
        priceCurrency: "AUD",
      },
    },
  };

  // WebPage schema for better page-level SEO
  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${siteUrl}/#webpage`,
    url: siteUrl,
    name: siteTitle,
    description: siteDescription,
    isPartOf: {
      "@id": `${siteUrl}/#website`,
    },
    about: {
      "@id": `${siteUrl}/#organization`,
    },
    inLanguage: "en-AU",
    datePublished: "2024-01-01",
    dateModified: "2025-12-05",
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: siteUrl,
        },
      ],
    },
  };

  // Professional Service schema
  const professionalServiceSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${siteUrl}/#professionalservice`,
    name: "FullScale Marketing - Tradie Digital Marketing",
    description: "Specialist digital marketing services for Australian tradies, builders, plumbers, electricians and home service businesses.",
    url: siteUrl,
    telephone: "+61",
    email: "info@fullscalemarketing.com.au",
    priceRange: "$$",
    areaServed: {
      "@type": "Country",
      name: "Australia",
    },
    serviceType: [
      "SEO for Tradies",
      "Google Ads Management",
      "Facebook Advertising",
      "Website Design for Tradies",
      "Google Reviews Management",
      "Lead Generation",
    ],
  };

  return (
    <>
      <Helmet>
        <title>{siteTitle}</title>
        <meta name="description" content={siteDescription} />
        
        {/* Additional SEO Meta Tags */}
        <meta name="keywords" content="marketing for tradies, tradie marketing, digital marketing for tradies, construction marketing Australia, home services marketing, local SEO tradies, Google Ads tradies, Facebook Ads tradies, tradie website design, Google reviews tradies, lead generation tradies, best marketing agency tradies Australia, digital marketing agency Sydney, marketing for builders, plumber marketing, electrician marketing" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content={siteName} />
        <meta name="geo.region" content="AU" />
        <meta name="geo.placename" content="Australia" />
        <link rel="canonical" href={siteUrl} />
        
        {/* Open Graph Meta Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={siteUrl} />
        <meta property="og:title" content={siteTitle} />
        <meta property="og:description" content={siteDescription} />
        <meta property="og:image" content={siteImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content={siteName} />
        <meta property="og:locale" content="en_AU" />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={siteUrl} />
        <meta name="twitter:title" content={siteTitle} />
        <meta name="twitter:description" content={siteDescription} />
        <meta name="twitter:image" content={siteImage} />
        
        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(professionalServiceSchema)}
        </script>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main>
          <HeroSection />
          <ProblemSection />
          <PromiseSection />
          <SystemSection />
          <ComparisonSection />
          <WhyWorksSection />
          <ResultsSection />
          <TradesSection />
          <ProcessSection />
          <FitSection />
          <FAQSection />
          <ContactSection />
        </main>
        <Footer />
        <MobileStickyCTA />
      </div>
    </>
  );
};

export default Index;
